import { Base64Util, BlobUtil, getThemeColor, MimeTypes, MixinUtil, ModelRow, StringUtil } from "@mcleod/core";
import { getImageModule, ImageName } from "@mcleod/images";
import { BusyMixin } from "../../mixins/BusyMixin";
import { BusyMixinInterface } from "../../mixins/BusyMixinInterface";
import { Component } from "../../base/Component";
import { DesignableObjectLogManager } from "../../logging/DesignableObjectLogManager";
import { ComponentPropDefinitions } from "../../base/ComponentProps";
import { ComponentTypes } from "../../base/ComponentTypes";
import { ImagePageControls } from "./ImagePageControls";
import { ImagePropDefinitions, ImageProps } from "./ImageProps";
import { ImageStyles } from "./ImageStyles";
import { ImageType } from "./ImageType";
import { ImageZoomControls } from "./ImageZoomControls";
import TIFF from 'tiff.js';

const log = DesignableObjectLogManager.getLogger("components.image.Image");

export class Image extends Component implements ImageProps {
    private _fill: string;
    private _imageBytes: string;
    private _imageType: ImageType;
    private _pageControls: ImagePageControls;
    private _zoomControls: ImageZoomControls;
    private _name: ImageName;
    private _rotation: number;
    private _stroke: string;
    private _strokeWidth: number;
    private _transition: string;
    private _rotate: boolean;
    private nameBeforeBusy: ImageName;
    private paths: SVGPathElement[];
    private tiff: TIFF;
    private _numPages: number;
    private _pageIndex: number;
    private _hideOnNullDataValue: boolean;

    constructor(props?: Partial<ImageProps>) {
        if (props != null && (props.imageType === ImageType.IMG || props.imageType === ImageType.TIFF)) {
            super("img", props);
            this._imageType = props.imageType;
            delete props.imageType;
        }
        else if (props != null && (props.imageType === ImageType.PDF)) {
            super("object", props);
            this._imageType = props.imageType;
            delete props.imageType;
        }
        else {
            super("div");
            (this as any)._element = document.createElementNS("http://www.w3.org/2000/svg", "svg");
            this._initElement(props);
        }
        this._element.setAttribute("class", ImageStyles.imageBase);
        this.setProps(props);
    }

    setProps(props: Partial<ImageProps>) {
        super.setProps(props);
    }

    get name(): ImageName {
        return this._name;
    }

    set name(value: ImageName) {
        this.resetMultiPageAttributes();
        if (this._name === value)
            return;
        this._name = value;
        if (this.nameBeforeBusy != null && !this.busy) {
            this.nameBeforeBusy = value;
        }
        if (value == null || value.length === 0) {
            this._element.innerHTML = "";
            return;
        }
        const module = getImageModule(value);
        if (module == null) {
            log.info(this, "Could not find module for image " + value);
            return;
        }
        const props = (module as any).props;
        if (props != null)
            for (const key in props) {
                if (key === "fillRule")
                    this._element.setAttribute("fill-rule", props[key]);
                else if (key !== "innerHTML")
                    this._element.setAttribute(key, props[key]);
            }
        if (props == null || props.viewBox == null)
            this._element.setAttribute("viewBox", "0 0 24 24");
        if ((module as any).innerHTML != null) {
            // Maybe we can start setting ids on each path defined in the innerHTML const.
            // Then we can apply props to specific paths rather than all paths.
            this._element.innerHTML = (module as any).innerHTML;
            this.paths = Array.from(this._element.querySelectorAll("path"));
        }
        else {
            this._element.innerHTML = "";
            const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
            path.setAttribute("d", module.path);
            this._element.appendChild(path);
            if (module.pathAttributes != null) {
                for (const key in module.pathAttributes) {
                    const value = module.pathAttributes[key];
                    path.setAttribute(key, value);
                }
            }
            this.paths = [path];
        }
        this.syncPathProps();
    }

    set rotation(degrees: number) {
        this._rotation = degrees;
        requestAnimationFrame(() => {
            if (degrees == null)
                this._element.style.transform = "";
            else
                this._element.style.transform = "rotate(" + degrees + "deg)";
        });
    }

    get rotation(): number {
        return this._rotation;
    }

    public rotateWithTransition(rotation: number, speed: number) {
        this.transition = Image.resolveTransitionValue(speed);
        this.rotation = rotation;
    }

    get transition(): string {
        return this._transition;
    }

    set transition(value: string|number) {
        this._transition = Image.resolveTransitionValue(value);
        this._element.style.transition = (this._transition == null) ? "" : this._transition;
    }

    static resolveTransitionValue(value: string|number) {
        return (typeof value === "number") ? `transform ${value}ms ease-in-out` : value;
    }

    get stroke(): string {
        return this._stroke;
    }

    set stroke(value: string) {
        this._stroke = value;
        this.syncStroke();
    }

    get fill(): string {
        return this._fill;
    }

    set fill(value: string) {
        this._fill = value;
        this._element.style.fill = getThemeColor(value);
        this.syncFill();
    }

    private syncFill() {
        this.paths?.forEach((path) => path.style.fill = this._element.style.fill);
    }

    get strokeWidth(): number {
        return this._strokeWidth;
    }

    set strokeWidth(value: number) {
        this._strokeWidth = value;
        this.syncStroke();
    }

    private syncStroke() {
        this.paths?.forEach((path) => {
            if (this._strokeWidth != null) {
                const stroke = this._stroke || "currentcolor";
                path.setAttribute("stroke", getThemeColor(stroke));
                path.setAttribute("stroke-width", this._strokeWidth.toString());
            } else {
                path.removeAttribute("stroke-width");
                if (this._stroke != null) {
                    path.setAttribute("stroke", getThemeColor(this._stroke));
                } else {
                    path.removeAttribute("stroke");
                }
            }
        });
    }

    private syncPathProps() {
        this.syncStroke();
        this.syncFill();
    }

    set preserveAspectRatio(value: boolean) {
        if (value)
            this._element.style.objectFit = "contain";
        else
            this._element.style.objectFit = "";
    }

    get imageBytes(): string {
        return this._imageBytes;
    }

    set imageBytes(value: string) {
        try {
            this.resetMultiPageAttributes();
            this._imageBytes = value;
            if (this.imageType === ImageType.IMG) {
                if (value != null && !value.startsWith("data:image"))
                    value = "data:image/jpeg;base64," + value;
                this._element.setAttribute("src", value);
            }
            else if (this.imageType === ImageType.TIFF) {
                const arrayBuffer = Base64Util.toArrayBuffer(this._imageBytes);
                this.tiff = new TIFF({ buffer: arrayBuffer });
                this.numPages = this.tiff.countDirectory();
                this.pageIndex = 0;
                this.displayCurrentPage();
            }
            else if (this.imageType === ImageType.PDF) {
                const blob = BlobUtil.toBlob(this._imageBytes, MimeTypes.PDF)
                let fileURL = URL.createObjectURL(blob);
                fileURL += "#toolbar=0&navpanes=0";
                this._element.setAttribute("data", fileURL);
            }
            else
                throw new Error("Cannot set imageBytes on an SVG image.");
        } finally {
            this.resetToFirstPage();
            this.resetZoom();
        }
    }

    get pageIndex(): number {
        return this._pageIndex;
    }

    private set pageIndex(value: number) {
        this._pageIndex = value;
    }

    get imageType(): ImageType {
        return this._imageType;
    }

    private resetMultiPageAttributes() {
        this.tiff = null;
        this.numPages = null;
        this.pageIndex = null;
    }

    get numPages(): number {
        return this._numPages == null ? 1 : this._numPages;
    }

    private set numPages(value: number) {
        this._numPages = value;
    }

    get isMultiPage(): boolean {
        return this.numPages > 1;
    }

    public get pageControls(): ImagePageControls {
        this.syncPageControls();
        return this._pageControls;
    }

    private syncPageControls() {
        if (this.isMultiPage === true) {
            if (this._pageControls == null) {
                this._pageControls = new ImagePageControls(this);
            }
        } else {
            this._pageControls = null;
        }
    }

    public resetToFirstPage() {
        // Only reset to the first page when the page controls have already been created (meaning we know the screen is
        // displaying them to the user).
        this._pageControls?.reset();
    }

    get zoomControls(): ImageZoomControls {
        this.syncZoomControls();
        return this._zoomControls;
    }

    private syncZoomControls() {
        if (this.imageType === ImageType.IMG || this.imageType === ImageType.TIFF) {
            if (this._zoomControls == null) {
                this._zoomControls = new ImageZoomControls(this);
            }
            this._zoomControls.enableAll(this.isEmpty() === false);
        } else {
            this._zoomControls = null;
        }
    }

    public resetZoom() {
        // Only reset the zoom when the zoom controls have already been created (meaning we know the screen is
        // displaying them to the user).
        this._zoomControls?.resetZoom();
    }

    get hideOnNullDataValue(): boolean {
        return this._hideOnNullDataValue;
    }

    set hideOnNullDataValue(value: boolean) {
        this._hideOnNullDataValue = value;
    }

    protected override displayComponentData(data: ModelRow, allData: ModelRow[], rowIndex: number): void {
        if (this._hideOnNullDataValue && this.field != null && data != null) {
            this.visible = !data.isNull(this.field);
        }
    }

    /**
     * This method will change the image to display a particular page within that image.
     *
     * If the provided page index is too high or too low, the first or last page will be displayed.
     * @param pageIndex The index of the page to be displayed
     * @returns The index of the page that was displayed
     */
    goToPage(pageIndex: number): number {
        const oldPageIndex = this.pageIndex;
        if (this.isMultiPage !== true || pageIndex == null)
            return oldPageIndex;
        this.setPageIndex(pageIndex);
        if (this.pageIndex !== oldPageIndex)
            this.displayCurrentPage();
        return this.pageIndex;
    }

    /**
     * Displays a page within the image based on a delta that is relative to the current page.
     * @param delta The change relative to the current page index.  For example, provide 1 to display the next page,
     *              or -1 to display the previous page.
     * @returns The index of the page that was displayed
     */
    advancePage(delta: number): number {
        if (delta == null)
            return this.pageIndex;
        return this.goToPage(this.pageIndex + delta);
    }

    /**
     * Updates the <img> element to display the page specified by the current page index.
     */
    private displayCurrentPage() {
        this.tiff.setDirectory(this.pageIndex);
        const canvas = this.tiff.toCanvas();
        const dataUrl = canvas.toDataURL();
        this._element.setAttribute("src", dataUrl);
    }

    /**
     * This method will update the page index value, but only to a valid value:
     * - If the provided page index value is too low, the pageIndex value will be set to 0.
     * - If the provided page index value is too high, the pageIndex value will be set to the last page in the image.
     * @param index The page index value that should be displayed.
     */
    private setPageIndex(value: number) {
        this.pageIndex = value;
        if (this.pageIndex < 0)
            this.pageIndex = 0;
        else {
            const lastPageIndex = this.getLastPageIndex();
            if (this.pageIndex > lastPageIndex)
                this.pageIndex = lastPageIndex;
        }
    }

    getLastPageIndex(): number {
        if (this.isMultiPage !== true)
            return 0;
        return Math.max(0, (this.numPages - 1));
    }

    /**
     * This method determines if the provided pageIndex value is valid for this image.
     * @param pageIndex The page index value to test.
     * @returns A number or null:
     * - A value of 0 signifies that the pageIndex value is valid.
     * - A value of -1 signifies that the pageIndex value is too low (less than zero).
     * - A value of 1 signifies that the pageIndex value is too hight (greater than the highest page index in the
     *   image).
     * - A value of null signifies that the image is not a multi-page image.
     */
    isValidPageIndex(pageIndex: number): number {
        if (this.isMultiPage !== true)
            return null;
        if (pageIndex < 0)
            return -1;
        if (pageIndex > this.getLastPageIndex())
            return 1;
        return 0;
    }

    public override getPropertyDefinitions(): ComponentPropDefinitions {
        return ImagePropDefinitions.getDefinitions();
    }

    get rotate() {
        return this._rotate == null ? false : this._rotate;
    }

    set rotate(value: boolean) {
        this._rotate = value;
        if (value)
            this._element.classList.add(ImageStyles.rotate);
        else
            this._element.classList.remove(ImageStyles.rotate);
    }

    protected applyBusy() {
        if (this.busy === true) {
            this.nameBeforeBusy = this._name;
            this.name = ImageName.spinner;
        }
        else {
            this.name = this.nameBeforeBusy;
            delete this.nameBeforeBusy;
        }
        this.rotate = this.busy;
    }

    override get serializationName() {
        return "image";
    }

    override get properName(): string {
        return "Image";
    }

    override getBasicValue(): any {
        return this.name;
    }

    public static createFileBasedImage(props: Partial<ImageProps>, fileName: string) {
        if (props.imageType == null) {
            props.imageType = this.getImageTypeForFileName(fileName);
        }
        return new Image(props);
    }

    public static getImageTypeForFileName(fileName: string): ImageType {
        if (fileName?.endsWith(".tiff")) {
            return ImageType.TIFF;
        }
        if (fileName?.endsWith(".pdf")) {
            return ImageType.PDF;
        }
        return ImageType.IMG;
    }

    public override isEmpty(): boolean {
        return StringUtil.isEmptyString(this.imageBytes);
    }
}

// Apply mixins at runtime
MixinUtil.applyMixins(Image, [ BusyMixin ]);

// TypeScript interface declaration for mixin integration
export interface Image extends BusyMixinInterface {
}

ComponentTypes.registerComponentType("image", {
    constructor: Image.prototype.constructor
});
